<template>
    <b-table :data="testCases" ref="table" :mobile-cards="false" hoverable striped bordered
             v-bind="$attrs" v-on="$listeners" sticky-header height="80vh" class="test-cases-table">
        <template #empty>
            <b><slot name="empty-message">No results found</slot></b>
        </template>
        <template #bottom-left>
            <b>Test cases:</b>
        </template>

        <b-table-column field="test" label="Test"
                        cell-class="fix-sticky-z-index"
                        width="60" sticky class="test-cases-table-test-column">
            <template v-slot:header="{ column }">
                <div class="test-case-header">
                    <b>{{ column.label }}</b>
                    <br>
                    <b-button v-if="jiraSummary === null"
                              :loading="jiraSummaryLoading"
                              size="is-small"
                              type="is-info is-light"
                              @click="onJiraSummaryClick">
                        Fetch JIRA issues
                    </b-button>
                </div>
            </template>

            <template v-slot="props">

              <v-popover popover-class="v-tooltip" placement="right" style="padding-right: 0.5em; display: inline-block; ">
                    <b-taglist style="cursor: pointer" @click.native="onJiraButtonClick(props.row.id)" attached>
                        <b-tag type="is-info">
                            <b-icon icon="jira" size="is-small"/>
                        </b-tag>
                        <template v-if="jiraSummary && jiraSummary[props.row.id]">
                            <b-tag type="is-danger">{{jiraSummary[props.row.id].issues}}</b-tag>
                            <b-tag>{{jiraSummary[props.row.id].secondary_issues}}</b-tag>
                        </template>
                    </b-taglist>

                        <template slot="popover">
                            <i v-if="jiraIssues == null">
                                Loading...
                            </i>
                            <template v-else>
                                <div style="max-width: 60vw; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                                     v-if="jiraIssues.has_issues">
                                    <a v-for="issue in jiraIssues.issues" :href="issue.url" :key="issue.key" target="_blank"
                                       class="jira-issue-link">
                                        <b>{{ issue.key }}</b> {{ issue.summary }}
                                    </a>
                                    <a v-for="issue in jiraIssues.secondary_issues"
                                       :href="issue.url"
                                       :key="issue.key"
                                       target="_blank"
                                       style="opacity: 0.5"
                                       class="jira-issue-link">
                                        <b>{{ issue.key }}</b> {{ issue.summary }}
                                    </a>
                                </div>
                                <template v-else>
                                    <i>Issues not found</i>
                                </template>
                                <a :key="issue.url"
                                   target="_blank"
                                   class="jira-issue-link"
                                   style="margin-top: 0.5em"
                                   :href="issue.url"
                                   v-for="issue in generateNewJiraIssueUrls(`${props.row.class}.${props.row.name}`)">
                                    <b>+</b> Create issue in <b>{{issue.name}}</b>
                                </a>
                            </template>
                        </template>
                    </v-popover>

                <span :title="props.row.name">{{ props.row.class }}.{{ props.row.name }}</span>
            </template>
        </b-table-column>

            <template v-for="targetBuild in targetBuilds">
                <b-table-column :label="targetBuild.target"
                                :custom-key="targetBuild.id"
                                :key="targetBuild.id"
                                style="width: 20vh;">
                    <template v-slot:header="{ column }">
                        <div class="test-cases-table-target-build-header">
                            <div class="tooltip-target target-build-header" :title="targetBuild.target" @click="$emit('select-target-build', targetBuild.id)">
                                <span class="test-run-id">{{ column.label }}</span>
                                <span class="product">{{ targetBuild.product }}</span>
                                <span class="target-build-time">{{ new Date(targetBuild.started_at) | moment("D.MM.YYYY HH:mm") }}</span>
                                <span v-if="targetBuild.filesystem" class="target-build-time"> FS: <b> {{ targetBuild.filesystem }} </b></span>
                            </div>
                            <div class="container target-build-footer">
                                <div class="level">
                                    <CiServiceIcon v-for="ci_service in targetBuild.ci_services"
                                                   :key="ci_service" :ci-service="ci_service"/>
                                    <BuildTriggerTypeIcon v-for="build_type in targetBuild.build_types"
                                                          :key="build_type" :type="build_type"/>
                                    <button class="delete delete-target-build-button"
                                            @click="$emit('delete-target-build', targetBuild.id)"/>

                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-slot="props">
                        <div class="test-result-container">
                            <div v-for="testResult in getTestResults(props.row.id, targetBuild.id)"
                                 :key="testResult.id"
                                 @click="onSelectTestResult(props.row.id, targetBuild.id)">
                                <test-summary-result :test-result="testResult"/>
                            </div>
                        </div>
                    </template>
                </b-table-column>
            </template>

    </b-table>
</template>

<script>
    import TestSummaryResult from "./TestSummaryResult";
    import CiServiceIcon from "@/components/CiServiceIcon";
    import BuildTriggerTypeIcon from "@/components/BuildTriggerTypeIcon";
    import {JIRA_PROJECTS, JIRA_URL} from "@/utils/constants";
    import {generateNewJiraIssueUrl} from "@/utils/helpers";

    export default {
        name: "TestCasesTable",
        props: {
            testCases: Array,
            testResults: Array,
            targetBuilds: Array,
            fetchJiraIssues: Function,
            fetchJiraSummary: Function,
        },
        data() {
            return {
                currentTestResult: null,
                isTestResultModalActive: false,
                activeJiraTooltip: null,
                jiraIssues: null,
                jiraSummaryLoading: false,
                jiraSummary: null,
            }
        },
        watch: {
            '$attrs.currentPage': function() {
                this.jiraSummary = null;
            },
        },
        methods: {
            getTestResults(testCaseId, targetBuildId) {
                return this.testResults.filter(item => {
                    return item.target_build_id === targetBuildId && item.test_case_id === testCaseId;
                });
            },
            onSelectTestResult(testCaseId, targetBuildId) {
                this.$emit('select-test-result', { testCaseId, targetBuildId });
            },
            async onJiraButtonClick(id) {
                if (this.activeJiraTooltip === id) {
                    this.activeJiraTooltip = null;
                } else {
                    this.activeJiraTooltip = id;
                    this.jiraIssues = null;
                    this.jiraIssues = await this.fetchJiraIssues(id);
                }
            },
            generateNewJiraIssueUrls(summary) {
                return JIRA_PROJECTS.map(project => {
                    return  {
                        url: generateNewJiraIssueUrl(JIRA_URL, project.pid, project.issueType, summary),
                        name: project.name,
                    }
                })
            },
            async onJiraSummaryClick() {
                this.jiraSummaryLoading = true;
                const startIndex = (this.$attrs["current-page"] - 1) * this.$attrs["per-page"];
                const endIndex = startIndex + this.$attrs["per-page"];
                const ids = this.testCases.slice(startIndex, endIndex).map(testCase => testCase.id);
                this.jiraSummary = await this.fetchJiraSummary(ids);
                this.jiraSummaryLoading = false;
            }
        },
        components: {
            TestSummaryResult,
            CiServiceIcon,
            BuildTriggerTypeIcon,
        },
    }
</script>

<style lang="scss" scoped>
    .test-cases-table-test-column {
        max-width: 400px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .test-run-id {
        font-weight: bold;
        cursor: pointer;
        font-size: 0.75em;
        &:hover {
            text-decoration: underline;
        }
        text-overflow: ellipsis;
        overflow: hidden;
        width: 100%;
    }
    .test-run-name {
        font-weight: normal;
        font-size: 0.75em;
    }
    .product {
        font-weight: normal;
        font-size: 0.8em;
    }

    .test-result-container {
        &:hover, &:focus, &:active {
            background-color: $grey-lightest;
        }
        & > div {
            display: flex;
            align-items: center;
            justify-content: center;
            /*flex-wrap: wrap;*/
            flex-direction: row;
        }
    }

    .test-cases-table {
        margin-bottom: 5px;
    }

    .test-cases-table-target-build-header {
        display: flex;
        flex-direction: column;
        width: 6vw;
    }

    .jira-issue-link {
        display: block;
        color: white;
        &:hover {
            text-decoration: underline;
            color: white;
        }
    }

    .test-case-header {
        display: flex;
        flex-direction: column;
        align-items: start;
    }

    .target-build-header {
        display: flex;
        flex-direction: column;
        margin: 0.75em 0;
    }

    .target-build-footer {
        height: 24px;
        padding: 0;
        line-height: 1;
        width: 100%;
    }

    .target-build-time {
        font-size: 0.65em;
        color: $grey;
        font-weight: normal;
    }

    .delete-target-build-button {
        opacity: 0;
        transition: opacity .15s ease;
    }

    th:hover .delete-target-build-button {
        opacity: 1;
    }
</style>